import { getResizedImage } from 'shared-helpers/common';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { AchievementIcon } from 'components/achievements';
import { CalendarEventDateString } from 'components/calendar';
import {
  LoadingIndicator,
  Owners,
  PublishedProductText
} from 'components/common';
import { Icon } from 'components/common/icons';
import { HomeDetailsItem } from '.';

import type { GetPendingRequestsRes } from 'shared-types/responses';

const HomeDetailsPendingRequests = () => {
  const { messages, theme, user } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const { data, loading } = useApiData<GetPendingRequestsRes>({
    onlyIf: Boolean(user?.id),
    url: '/admin/pending-requests'
  });

  return loading ? (
    <LoadingIndicator />
  ) : (
    <div className="requests">
      {data?.pendingSupervisorRequests > 0 ? (
        <HomeDetailsItem
          href="/my-team/follow/achievements"
          category="default"
          text={getMessageWithPlaceholders({
            message: messages.adminCountPendingRequests,
            placeholders: { number: data.pendingSupervisorRequests }
          })}
        >
          <div className="picture">
            <Icon
              icon="sitemap"
              style={{ color: '#555', padding: 9, height: 22, width: 22 }}
            />
          </div>

          <div className="inner">
            <div className="title">{messages.myTeamPage}</div>
          </div>
        </HomeDetailsItem>
      ) : null}

      {data?.pendingCompetencyRequests > 0 ? (
        <HomeDetailsItem
          href="/competencies/pending-requests"
          category="default"
          text={getMessageWithPlaceholders({
            message: messages.adminCountPendingRequests,
            placeholders: { number: data.pendingCompetencyRequests }
          })}
        >
          <div className="picture">
            <Icon
              icon="medal"
              style={{ color: '#555', padding: 9, height: 22, width: 22 }}
            />
          </div>

          <div className="inner">
            <div className="title">{messages.commonCompetencies}</div>
          </div>
        </HomeDetailsItem>
      ) : null}

      {data?.pendingRequests.map((product) =>
        product.requestType === 'course' ? (
          <HomeDetailsItem
            href={`/admin/courses/${product.slug}/share`}
            key={`course-${product.id}`}
            category="default"
            text={getMessageWithPlaceholders({
              message: messages.adminCountPendingRequests,
              placeholders: { number: product.numberOfPendingRequests }
            })}
          >
            <div
              className="picture"
              style={{
                backgroundImage: `url(${getResizedImage({
                  url: product.picture,
                  size: 480
                })})`,
                backgroundPosition: product.picturePosition
              }}
            />

            <div className="inner">
              <div className="title">{product.title}</div>
              <div className="info">
                <Owners owners={product.owners} />

                <PublishedProductText
                  endDate={product.endDate as string}
                  startDate={product.startDate as string}
                />
              </div>
            </div>
          </HomeDetailsItem>
        ) : product.requestType === 'event' ? (
          <HomeDetailsItem
            href={`/calendar/${product.id}/participants`}
            key={`event-${product.id}`}
            category="default"
            text={getMessageWithPlaceholders({
              message: messages.adminCountPendingRequests,
              placeholders: { number: product.numberOfPendingRequests }
            })}
          >
            {product.imageUrl ? (
              <div
                className="picture"
                style={{ backgroundImage: `url(${product.imageUrl})` }}
              />
            ) : (
              <div className="picture">
                <Icon
                  icon={['far', 'calendar-days']}
                  style={{ color: '#555', padding: 9, height: 22, width: 22 }}
                />
              </div>
            )}

            <div className="inner">
              <div className="title">{product.title}</div>
              <div className="info">
                <Owners owners={product.owners} />

                <CalendarEventDateString
                  startDate={product.startDate}
                  endDate={product.endDate}
                />
              </div>
            </div>
          </HomeDetailsItem>
        ) : (
          <HomeDetailsItem
            href={`/admin/achievements/${product.id}/users`}
            key={`achievement-${product.id}`}
            category="default"
            text={getMessageWithPlaceholders({
              message: messages.adminCountPendingRequests,
              placeholders: { number: product.numberOfPendingRequests }
            })}
          >
            <AchievementIcon size="small" type={product.type} />

            <div className="inner">
              <div className="title">{product.title}</div>
              <div className="info">
                <Owners owners={product.owners} />

                {achievementTypes[product.type].title}
              </div>
            </div>
          </HomeDetailsItem>
        )
      )}

      <style jsx>{`
        .requests {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .picture {
          background-color: ${theme.lightGrey};
          background-size: cover;
          border-radius: 8px;
          flex-shrink: 0;
          height: 40px;
          width: 40px;
        }
        .inner {
          flex-grow: 1;
        }
        .title {
          font-size: 1.6rem;
          font-weight: 700;
        }
        .info {
          color: ${theme.textColorLighter};
          display: flex;
          flex-wrap: wrap;
          font-size: 1.4rem;
          font-weight: 600;
          gap: 5px 40px;
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
};

export default HomeDetailsPendingRequests;
